import { downloadFile, http, upload, type UploadOptions } from '@/lib/http';
import type { PagedResult, UtcDateTime } from './types';

// ---------- Documents ----------
export interface DocumentDto {
  id: number;
  documentTypeCode: string;
  sourceType: 'UPLOAD' | 'EXTERNAL_URL' | 'GENERATED' | 'INTEGRATION' | string;
  confidentialityLevel: string;
  title: string;
  currentVersionId: number | null;
  versionNo: number;
  fileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  sha256: string | null;
  externalUrl: string | null;
  verificationStatus: string;
  scanStatus: string;
  uploadedAtUtc: UtcDateTime;
  uploadedBy: number;
}

/** Entity types the API accepts for attachments (PlatformEndpoints.LinkableEntities). */
export type LinkableEntity =
  | 'Customer'
  | 'CustomerRepresentative'
  | 'Quotation'
  | 'QuotationVersion'
  | 'ApprovalInstance'
  | 'Lead'
  | 'Opportunity';

export const DOCUMENT_TYPE_LABEL: Record<string, string> = {
  CONTRACT_SIGNED: 'Hợp đồng đã ký',
  AMENDMENT: 'Phụ lục',
  INVOICE: 'Hóa đơn',
  PAYMENT_PROOF: 'Chứng từ thanh toán',
  BANK_STATEMENT: 'Sao kê ngân hàng',
  ACCEPTANCE: 'Biên bản nghiệm thu',
  TRAINING_MATERIAL: 'Tài liệu đào tạo',
  AUTHORIZATION_LETTER: 'Giấy ủy quyền',
  BUSINESS_REGISTRATION: 'Giấy ĐKKD',
  QUOTATION: 'Báo giá',
  COST_EVIDENCE: 'Chứng từ chi phí',
  QUOTATION_TEMPLATE: 'Mẫu báo giá',
  OTHER: 'Khác',
};

/** Accepted upload types (server sniffs bytes; this is only the hint + client pre-check). */
export const UPLOAD_ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx';
export const UPLOAD_MAX_BYTES = 25 * 1024 * 1024;

export const documentsApi = {
  list: (entityType: LinkableEntity, entityId: number) =>
    http.get<DocumentDto[]>('/documents/documents', { entityType, entityId }),
  types: () => http.get<string[]>('/documents/document-types'),
  upload: (
    file: File,
    meta: { documentTypeCode: string; title?: string; entityType?: LinkableEntity; entityId?: number },
    opts?: UploadOptions,
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('documentTypeCode', meta.documentTypeCode);
    if (meta.title) form.append('title', meta.title);
    if (meta.entityType) form.append('entityType', meta.entityType);
    if (meta.entityId != null) form.append('entityId', String(meta.entityId));
    return upload<DocumentDto>('/documents/documents', form, opts);
  },
  addExternalLink: (req: {
    url: string;
    sourceName?: string | null;
    documentTypeCode: string;
    title?: string | null;
    entityType?: LinkableEntity | null;
    entityId?: number | null;
  }) => http.post<DocumentDto>('/documents/external-links', req),
  download: (doc: Pick<DocumentDto, 'id' | 'fileName' | 'title'>, version?: number) =>
    downloadFile(`/documents/documents/${doc.id}/content${version ? `?version=${version}` : ''}`, doc.fileName ?? doc.title),
};

// ---------- Notifications ----------
export interface NotificationDto {
  id: number;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  createdAtUtc: UtcDateTime;
  readAtUtc: UtcDateTime | null;
}

export const notificationsApi = {
  list: (unreadOnly = false) => http.get<{ items: NotificationDto[]; unread: number }>('/platform/notifications', { unreadOnly }),
  /** Empty ids → mark all as read. */
  markRead: (ids?: number[]) => http.post<void>('/platform/notifications/read', ids ?? []),
};

// ---------- Status catalog ----------
export interface StatusDef {
  domain: string;
  code: string;
  nameVi: string;
  terminal: boolean;
}

export interface TransitionDef {
  domain: string;
  from: string;
  to: string;
}

export interface StatusCatalogResponse {
  version: string;
  statuses: StatusDef[];
  transitions: TransitionDef[];
}

export const statusApi = {
  catalog: () => http.get<StatusCatalogResponse>('/platform/status-catalog'),
};

// ---------- Audit ----------
export interface AuditEvent {
  id: number;
  occurredAtUtc: UtcDateTime;
  actorType: string;
  actorUserId: number | null;
  actorName: string | null;
  actorAvatarUrl?: string | null;
  onBehalfOfUserId: number | null;
  module: string;
  actionCode: string;
  entityType: string;
  entityId: string;
  beforeJson: string | null;
  afterJson: string | null;
  reasonCode: string | null;
  reasonText: string | null;
  outcome: 'SUCCESS' | 'FAILED' | 'BLOCKED' | string;
  errorCode: string | null;
  correlationId: string | null;
  traceId: string | null;
  sourceIp: string | null;
  clientApp: string | null;
  evidenceRefs: string | null;
  workflowInstanceId: number | null;
  policyVersionId: number | null;
  integrityHash: string;
}

export interface AuditQuery {
  /** Vietnam business dates yyyy-MM-dd (inclusive) on occurredAt; combinable with fromUtc/toUtc instants. */
  from?: string;
  to?: string;
  dateField?: string;
  fromUtc?: string;
  toUtc?: string;
  actorUserId?: number;
  entityType?: string;
  entityId?: string;
  actionCode?: string;
  outcome?: string;
  correlationId?: string;
  module?: string;
  page?: number;
  pageSize?: number;
}

export const auditApi = {
  search: (q: AuditQuery, signal?: AbortSignal) => http.get<PagedResult<AuditEvent>>('/platform/audit-events', { ...q }, { signal }),
};
